import React, { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

type VerifyStatus = 'verifying' | 'success' | 'error';

const VerifyEmail: React.FC = () => {
  const { verifyEmail, resendVerification, isLoading, error, clearError } = useAuth();
  const { token } = useParams<{ token: string }>();

  const [status, setStatus] = useState<VerifyStatus>('verifying');
  const [resendEmail, setResendEmail] = useState('');
  const [resendStatus, setResendStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  // React StrictMode runs effects twice in dev; never consume the token twice.
  const hasVerified = useRef(false);

  useEffect(() => {
    if (hasVerified.current) return;
    hasVerified.current = true;

    const run = async () => {
      if (!token) {
        setStatus('error');
        return;
      }
      clearError();
      try {
        await verifyEmail(token);
        setStatus('success');
      } catch {
        setStatus('error');
      }
    };

    run();
  }, [token, verifyEmail, clearError]);

  const handleResend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resendEmail) return;
    setResendStatus('sending');
    try {
      await resendVerification(resendEmail);
      setResendStatus('sent');
    } catch {
      setResendStatus('error');
    }
  };

  return (
    <div className="auth-background">
      <div className="auth-background-overlay"></div>
      <div className="auth-card">
        <div className="auth-header">
          <div className="auth-logo">
            <img src="/logo-commission-fr.png" alt="CNCLT" />
          </div>
          <p className="auth-subtitle text-gray-600">
            {status === 'verifying' && 'Vérification de votre email...'}
            {status === 'success' && 'Email vérifié'}
            {status === 'error' && 'Vérification impossible'}
          </p>
        </div>

        {status === 'verifying' && (
          <div className="flex items-center justify-center py-6">
            <div className="auth-spinner mr-2"></div>
            <span className="text-gray-600">Vérification en cours...</span>
          </div>
        )}

        {status === 'success' && (
          <>
            <div className="auth-alert auth-alert-success">
              <p>Votre adresse email a été vérifiée avec succès. Vous pouvez maintenant vous connecter.</p>
            </div>
            <div className="text-center">
              <Link to="/login" className="auth-button-primary block">
                Se connecter
              </Link>
            </div>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="auth-alert auth-alert-error">
              <p>
                Ce lien de vérification est invalide ou a expiré. Vous pouvez demander un
                nouveau lien ci-dessous.
              </p>
            </div>

            <form className="auth-form" onSubmit={handleResend}>
              <div className="auth-form-group">
                <label className="auth-form-label">Adresse email</label>
                <input
                  type="email"
                  className="auth-form-input"
                  placeholder="votre@email.com"
                  value={resendEmail}
                  onChange={(e) => {
                    setResendEmail(e.target.value);
                    if (resendStatus === 'error') setResendStatus('idle');
                    if (error) clearError();
                  }}
                  required
                  disabled={isLoading}
                />
              </div>

              {resendStatus === 'sent' && (
                <div className="auth-alert auth-alert-success">
                  <p>Si un compte non vérifié existe avec cette adresse, un nouveau lien a été envoyé.</p>
                </div>
              )}
              {resendStatus === 'error' && (
                <p className="auth-form-error">Une erreur est survenue. Veuillez réessayer.</p>
              )}

              <button
                type="submit"
                className={`auth-button-primary ${isLoading ? 'auth-loading' : ''}`}
                disabled={isLoading || resendStatus === 'sending'}
              >
                {resendStatus === 'sending' ? 'Envoi en cours...' : 'Renvoyer le lien de vérification'}
              </button>
            </form>

            <div className="text-center">
              <Link to="/login" className="auth-link text-sm">
                Retour à la connexion
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default VerifyEmail;
